import React, { useMemo, useState } from "react";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Send, X } from "lucide-react";
import { discussTrayItem, type ExecCheckRecord, type TrayDiscussionTurn, type TrayItem } from "../api/workbench";
import { fmtUsd } from "../services/costEstimate";
import { groupMemoFields, MEMO_CATEGORIES, memoKeyChanges } from "./memoViews";
import { formatTrackerDate, trackerEvents, type TrackerEvent } from "./trackerViews";
import { formatOutputSources, reviewTrayPresentation, trackerPresentation, withoutEmailReferences } from "./outputPresentation";

/* ===================================================================
   Run-result panel — the one way run output is inspected. Used by the
   Bundle view's single-email runs and by the simulation's right panel
   (which adds an Eval tab via extraTabs). Summary strip on top, then
   left-nav sections: Review Tray / Memo / Documents / Trackers /
   Checks / Usage.
=================================================================== */

export interface MemoFieldVal {
  value: string;
  source?: string;
  history?: Array<{ value: string; source?: string }>;
  stale?: boolean;
}

export interface RunPanelData {
  routes: Array<{ workstreamId: string; confidence: number }>;
  unrouted?: boolean;
  tray: TrayItem[];
  memos: Record<string, { phase: string | null; fields: Record<string, MemoFieldVal> }>;
  register: Record<string, Array<{ name?: string; doc_type?: string | null; current_version?: number; versions?: unknown[]; authority_conflict?: boolean }>>;
  trackers: Record<string, Record<string, Array<Record<string, unknown>>>>;
  checks: ExecCheckRecord[];
  usage: { cost: number; perAgent: Record<string, { cost: number; calls: number; model: string }> };
  saDispatches: Array<{ agentId: string; ok: boolean; runId?: string | null; summary?: string; error?: string }>;
  warnings: string[];
  degraded?: boolean;
}

export interface RunResultPanelProps {
  data: RunPanelData;
  wsName: (id: string) => string;
  /** τ per agent id — the pinned bundle's values, for the Checks section. */
  tauFor: (agentId: string) => number | null;
  agentName: (agentId: string) => string;
  onResolve: (item: TrayItem, action: "approve" | "reject" | "choose" | "answer" | "acknowledge", extra?: { choice?: string; text?: string; note?: string }) => void;
  onRuling: (item: TrayItem, text: string) => void;
  onRecompute?: (wsId: string, field: string) => void;
  extraTabs?: Array<{ id: string; label: string; badge?: number; render: () => React.ReactNode }>;
  emptyHint?: string;
}

const AMBER = "var(--amber)";

function SupportingDetails({ summary, details }: { summary: string; details: string }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!details || details === summary) return null;
  return (
    <div className="output-supporting-details">
      <button className="lnk" type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        {open ? "Hide details" : "Show details"}
      </button>
      {open && <div className="output-detail-copy">{details}</div>}
    </div>
  );
}

function TrackerEventBody({ entry, wsName, currentWsId }: {
  entry: TrackerEvent;
  wsName: (id: string) => string;
  currentWsId?: string;
}): JSX.Element {
  const presentation = trackerPresentation(entry.event, entry.source, entry.date);
  const source = formatOutputSources(presentation.sources) || entry.source;
  return (
    <>
      <div className="tracker-date">{formatTrackerDate(entry.date)}</div>
      <div className="tracker-event-copy">
        <div className="tracker-event-title">{presentation.summary}</div>
        {currentWsId ? entry.workstreamIds.length > 1 && (
          <div className="tracker-workstream">Also: {entry.workstreamIds.filter((id) => id !== currentWsId).map(wsName).join(" · ")}</div>
        ) : (
          <div className="tracker-workstream">{entry.workstreamIds.map(wsName).join(" · ")}</div>
        )}
        <SupportingDetails summary={presentation.summary} details={presentation.details} />
      </div>
      <div className="tracker-meta">
        <span className="tracker-source">{source}</span>
        <span aria-hidden="true">·</span>
        <span className={`tracker-status ${entry.group}`}>
          {entry.group === "upcoming" ? "Upcoming" : entry.group === "completed" ? "Completed" : "Changed or cancelled"}
        </span>
      </div>
    </>
  );
}

function TrayCard(p: {
  item: TrayItem;
  agentName: (id: string) => string;
  wsName: (id: string) => string;
  onResolve: RunResultPanelProps["onResolve"];
  onRuling: RunResultPanelProps["onRuling"];
}): JSX.Element {
  const { item } = p;
  const [choice, setChoice] = useState<string>("");
  const [text, setText] = useState<string>("");
  const [discussOpen, setDiscussOpen] = useState(false);
  const [discussion, setDiscussion] = useState<TrayDiscussionTurn[]>([]);
  const [discussionInput, setDiscussionInput] = useState("");
  const [discussionBusy, setDiscussionBusy] = useState(false);
  const [discussionError, setDiscussionError] = useState("");
  const pending = item.status === "pending";
  const presentation = reviewTrayPresentation(item.title, item.detail, item.justification);
  const kindLabel =
    item.kind === "decision" ? "Decision" : item.kind === "choice" ? "Pick one" : item.kind === "input" ? "Needs your words" : "FYI";
  const sendDiscussion = async (): Promise<void> => {
    const question = discussionInput.trim();
    if (!question || discussionBusy) return;
    const prior = discussion.slice(-6);
    setDiscussion((turns) => [...turns, { role: "user", content: question }]);
    setDiscussionInput("");
    setDiscussionError("");
    setDiscussionBusy(true);
    try {
      const result = await discussTrayItem(item.id, question, prior);
      setDiscussion((turns) => [...turns, { role: "assistant", content: result.answer }]);
    } catch (e) {
      setDiscussionError(e instanceof Error ? e.message : "The discussion agent could not answer.");
    } finally {
      setDiscussionBusy(false);
    }
  };
  return (
    <div className={"tray-item" + (item.gate ? " gated" : "")} style={{ opacity: pending ? 1 : 0.65 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className="chip dep" style={{ fontWeight: 600 }}>{kindLabel}</span>
        <span style={{ fontWeight: 600, fontSize: 13.5, flex: 1, minWidth: 0 }}>{withoutEmailReferences(item.title)}</span>
        {item.recurrence.length > 0 && (
          <span className="chip" title="This question came up again on later emails; it stays as one item.">
            ×{item.recurrence.length + 1}
          </span>
        )}
        <span className="chip">{p.agentName(item.agentId)}</span>
        {item.matters.length > 1 && (
          <span className="chip">{item.matters.map(p.wsName).join(" · ")}</span>
        )}
        {item.status !== "pending" && (
          <span className="chip" style={item.status === "resolved" ? { background: "var(--acc-soft)", color: "var(--acc)" } : undefined}>
            {item.status}
          </span>
        )}
      </div>
      <div className="tert output-summary">{presentation.summary}</div>
      <SupportingDetails summary={presentation.summary} details={presentation.details} />
      {item.justification && (
        <div className="tert" style={{ fontSize: 11.5, fontStyle: "italic", marginBottom: 6 }}>
          Merged: {item.justification}
        </div>
      )}
      {presentation.sources.length > 0 && (
        <div className="output-source-footer">{formatOutputSources(presentation.sources)}</div>
      )}
      {pending && item.gate && (
        <div className="tr-pending">
          While you decide: emails keep flowing — only{" "}
          <span className="mono">{item.holdField || "this value"}</span> is held at its prior value; your call annotates it.
        </div>
      )}
      {pending && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {item.kind === "decision" && (
            <>
              <button className="btn btn-primary btn-sm" onClick={() => p.onResolve(item, "approve")}>
                <Check size={13} /> Approve
              </button>
              <button className="btn btn-sm" onClick={() => p.onResolve(item, "reject")}>
                <X size={13} /> Dismiss
              </button>
            </>
          )}
          {item.kind === "choice" && (
            <>
              {(item.options || []).map((o) => (
                <label key={o} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5 }}>
                  <input type="radio" name={`tray-${item.id}`} checked={choice === o} onChange={() => setChoice(o)} />
                  <span className="mono">{o}</span>
                </label>
              ))}
              <button className="btn btn-primary btn-sm" disabled={!choice} onClick={() => p.onResolve(item, "choose", { choice })}>
                Confirm
              </button>
            </>
          )}
          {item.kind === "input" && (
            <div style={{ width: "100%" }}>
              <textarea className="inp tarea" style={{ minHeight: 54, width: "100%" }} placeholder="Your answer — recorded into the matter state" value={text} onChange={(e) => setText(e.target.value)} />
              <button className="btn btn-primary btn-sm" style={{ marginTop: 6 }} disabled={!text.trim()} onClick={() => p.onResolve(item, "answer", { text })}>
                Record & release
              </button>
            </div>
          )}
          {item.kind === "notice" && (
            <button className="btn btn-sm" onClick={() => p.onResolve(item, "acknowledge")}>
              <Check size={13} /> Acknowledge
            </button>
          )}
          <span className="spacer" />
          <button className="lnk" style={{ fontSize: 11.5 }} onClick={() => setDiscussOpen((v) => !v)}>
            {discussOpen ? "Close discussion" : "Discuss"}
          </button>
          <button
            className="lnk"
            style={{ fontSize: 11.5 }}
            title="Record a matter-scoped ruling: future escalations matching this are suppressed."
            onClick={() => {
              const t = window.prompt("Don't ask again — record the standing ruling for this matter:", `No need to raise "${item.title}" again.`);
              if (t) p.onRuling(item, t);
            }}
          >
            Don't ask again
          </button>
        </div>
      )}
      {pending && discussOpen && (
        <div className="card" style={{ marginTop: 8, padding: 10, background: "var(--subtle)" }}>
          <div className="tert" style={{ fontSize: 11.5, marginBottom: 8 }}>
            Ask about this review item. Discussion is advisory and will not change the decision or matter state.
          </div>
          {discussion.map((turn, i) => (
            <div
              key={`${turn.role}-${i}`}
              style={{
                margin: "6px 0",
                padding: "7px 9px",
                borderRadius: 6,
                background: turn.role === "user" ? "var(--acc-soft)" : "var(--bg)",
                fontSize: 12.5,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              <b>{turn.role === "user" ? "You" : "Assistant"}</b><br />
              {turn.content}
            </div>
          ))}
          {discussionBusy && <div className="tert" style={{ fontSize: 12, margin: "6px 0" }}>Reviewing the source email…</div>}
          {discussionError && <div style={{ color: "var(--bad)", fontSize: 12, margin: "6px 0" }}>{discussionError}</div>}
          <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
            <textarea
              className="inp tarea"
              style={{ minHeight: 44, width: "100%" }}
              placeholder="Ask why this item was raised…"
              value={discussionInput}
              maxLength={2_000}
              disabled={discussionBusy}
              onChange={(e) => setDiscussionInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void sendDiscussion();
                }
              }}
            />
            <button
              className="btn btn-primary btn-sm"
              style={{ minHeight: 36 }}
              disabled={discussionBusy || !discussionInput.trim()}
              onClick={() => void sendDiscussion()}
            >
              <Send size={13} /> Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function RunResultPanel(p: RunResultPanelProps): JSX.Element {
  const { data } = p;
  const pendingTray = data.tray.filter((t) => t.status === "pending");
  const fired = data.checks.filter((c) => !c.skipped && !c.id.startsWith("_"));
  const skipped = data.checks.filter((c) => c.skipped);
  const [tab, setTab] = useState("tray");
  const [memoView, setMemoView] = useState<"current" | "changes">("current");
  const [trackerView, setTrackerView] = useState<"all" | "workstream">("all");
  const [skipsOpen, setSkipsOpen] = useState(false);

  const datedEvents = useMemo(() => trackerEvents(data.trackers), [data.trackers]);
  const uniqueDocuments = useMemo(() => {
    type DocumentRow = RunPanelData["register"][string][number] & { workstreamIds: string[] };
    const documents = new Map<string, DocumentRow>();
    Object.entries(data.register).forEach(([workstreamId, rows]) => rows.forEach((document) => {
      const key = `${String(document.doc_type || "").trim().toLowerCase()}|${String(document.name || "").trim().toLowerCase()}`;
      const existing = documents.get(key);
      if (!existing) {
        documents.set(key, { ...document, versions: [...(document.versions || [])], workstreamIds: [workstreamId] });
        return;
      }
      existing.workstreamIds = [...new Set([...existing.workstreamIds, workstreamId])];
      existing.current_version = Math.max(existing.current_version || 0, document.current_version || 0) || undefined;
      existing.authority_conflict = existing.authority_conflict || document.authority_conflict;
      existing.versions = [...new Map(
        [...(existing.versions || []), ...(document.versions || [])].map((version) => [JSON.stringify(version), version]),
      ).values()];
    }));
    return [...documents.values()];
  }, [data.register]);

  const sections: Array<{ id: string; label: string; badge?: number }> = [
    { id: "tray", label: "Review Tray", badge: pendingTray.length },
    { id: "memo", label: "Memo", badge: Object.values(data.memos).reduce((n, m) => n + Object.keys(m.fields || {}).length, 0) },
    { id: "docs", label: "Documents", badge: uniqueDocuments.length },
    { id: "track", label: "Trackers", badge: datedEvents.length },
    { id: "checks", label: "Checks", badge: fired.length },
    { id: "usage", label: "Usage" },
    ...(p.extraTabs || []).map((t) => ({ id: t.id, label: t.label, badge: t.badge })),
  ];

  const wsIds = useMemo(
    () => [...new Set([...Object.keys(data.memos), ...Object.keys(data.register), ...Object.keys(data.trackers)])],
    [data],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Summary strip — routing line, then service dispatch + cost */}
      <div className="rsum">
        <div className="l">
          <span>
            →{" "}
            {data.unrouted || !data.routes.length ? (
              <b style={{ color: "var(--sem)" }}>unassociated</b>
            ) : (
              data.routes.map((r, i) => (
                <React.Fragment key={r.workstreamId}>
                  {i > 0 && " + "}
                  <b>{p.wsName(r.workstreamId)}</b> {r.confidence.toFixed(2)}
                </React.Fragment>
              ))
            )}
          </span>
          <span>
            <b style={pendingTray.length ? { color: "var(--bad)" } : undefined}>{pendingTray.length}</b> for review
          </span>
          <span>
            <b>{fired.length}</b> fired · {skipped.length} skipped
          </span>
          {data.degraded && (
            <span className="chip" style={{ background: "var(--bad-soft)", color: "var(--bad)", fontWeight: 600 }} title="Something that should have contributed did not — see the notice below.">
              degraded
            </span>
          )}
        </div>
        <div className="l">
          {data.saDispatches.length > 0 && (
            <span>
              → <b>SA service</b> {data.saDispatches.filter((s) => s.ok).length}/{data.saDispatches.length} work package{data.saDispatches.length === 1 ? "" : "s"}
            </span>
          )}
          <span className="mono num">{fmtUsd(data.usage.cost)}</span>
        </div>
      </div>
      {data.warnings.length > 0 && (
        <div className="card" role="alert" style={{ padding: "8px 12px", marginBottom: 10, color: AMBER, fontSize: 12, lineHeight: 1.5 }}>
          <AlertTriangle size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
          {data.warnings.join(" · ")}
        </div>
      )}
      <div style={{ display: "flex", gap: 12, flex: 1, minHeight: 0 }}>
        {/* Left-nav — quiet rows, red badge only where a lawyer is needed */}
        <div className="wnav">
          {sections.map((s) => (
            <React.Fragment key={s.id}>
              <button className={tab === s.id ? "on" : ""} onClick={() => setTab(s.id)}>
                {s.label}
                {s.badge !== undefined && s.badge > 0 && (
                  <span className={"rbadge" + (s.id === "tray" ? "" : " mut")}>{s.badge}</span>
                )}
              </button>
              {s.id === "memo" && tab === "memo" && (
                <div className="memo-view-nav" aria-label="Memo views">
                  <div className="memo-view-note">Same information, different view</div>
                  {([["current", "Current Memo"], ["changes", "Key Changes"]] as const).map(([id, label]) => (
                    <button key={id} className={memoView === id ? "on" : ""} onClick={() => setMemoView(id)} aria-pressed={memoView === id}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {s.id === "track" && tab === "track" && (
                <div className="memo-view-nav" aria-label="Tracker views">
                  <div className="memo-view-note">Same events, different view</div>
                  {([[
                    "all", "All events",
                  ], ["workstream", "By workstream"]] as const).map(([id, label]) => (
                    <button key={id} className={trackerView === id ? "on" : ""} onClick={() => setTrackerView(id)} aria-pressed={trackerView === id}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        {/* Section body */}
        <div className="scroll" style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
          {tab === "tray" && (
            <>
              {data.tray.length === 0 && <div className="tert" style={{ fontSize: 12.5 }}>{p.emptyHint || "Nothing needs the lawyer yet."}</div>}
              {[...data.tray]
                .sort((a, b) => (a.status === "pending" ? 0 : 1) - (b.status === "pending" ? 0 : 1))
                .map((t) => (
                  <TrayCard key={t.id} item={t} agentName={p.agentName} wsName={p.wsName} onResolve={p.onResolve} onRuling={p.onRuling} />
                ))}
            </>
          )}
          {tab === "memo" && memoView === "current" && wsIds
            .filter((ws) => data.memos[ws] && Object.keys(data.memos[ws].fields || {}).length)
            .map((ws) => {
              const grouped = groupMemoFields(data.memos[ws].fields);
              return (
                <section className="memo-workstream" key={ws}>
                  <div className="memo-workstream-label">Workstream</div>
                  <div className="up memo-title">{p.wsName(ws)}</div>
                  {MEMO_CATEGORIES.map((category, index) => {
                    const entries = grouped[category.id];
                    const overviewRows = category.id === "overview" ? [
                      ["Matter name", p.wsName(ws), ""],
                      ...(data.memos[ws].phase ? [["Current phase", data.memos[ws].phase || "", ""]] : []),
                    ] : [];
                    if (!entries.length && !overviewRows.length) return null;
                    return (
                      <details className="memo-group" key={category.id} open={index < 3}>
                        <summary>{category.label}<span>{entries.length + overviewRows.length}</span></summary>
                        <table className="tbl memo-table"><tbody>
                          {overviewRows.map(([field, value, source]) => <tr key={field}><td>{field}</td><td>{value}</td><td>{source}</td></tr>)}
                          {entries.map(([field, value]) => (
                            <tr key={field}>
                              <td>{field}</td>
                              <td>
                                {value.value}
                                {value.stale && <button className="lnk memo-recompute" title="This value was derived while a lawyer decision was pending — recompute the affected email." onClick={() => p.onRecompute?.(ws, field)}>stale — Recompute</button>}
                              </td>
                              <td>{value.source || ""}</td>
                            </tr>
                          ))}
                        </tbody></table>
                      </details>
                    );
                  })}
                  <details className="memo-group">
                    <summary>Key documents<span>{(data.register[ws] || []).length}</span></summary>
                    {(data.register[ws] || []).length ? (
                      <table className="tbl memo-table"><tbody>
                        {(data.register[ws] || []).map((doc, index) => (
                          <tr key={`${doc.name || doc.doc_type}-${index}`}>
                            <td>{doc.name || doc.doc_type || "Document"}</td>
                            <td>{doc.doc_type || "—"}{doc.authority_conflict ? " · authority conflict" : ""}</td>
                            <td>{doc.current_version ? `v${doc.current_version}` : ""}</td>
                          </tr>
                        ))}
                      </tbody></table>
                    ) : <div className="memo-empty">No key documents registered yet.</div>}
                  </details>
                </section>
              );
            })}
          {tab === "memo" && memoView === "changes" && wsIds.filter((ws) => data.memos[ws]).map((ws) => {
            const changes = memoKeyChanges(data.memos[ws].fields);
            return (
              <section key={ws} className="memo-workstream memo-view-body">
                <div className="memo-workstream-label">Workstream</div>
                <div className="up memo-title">{p.wsName(ws)}</div>
                {changes.length ? changes.map((change) => (
                  <div className="memo-key-change" key={change.field}>
                    <div className="memo-key-change-title">{change.field}</div>
                    <div className="memo-key-change-steps">
                      {change.records.map((record, index) => (
                        <React.Fragment key={`${record.value}-${index}`}>
                          {index > 0 && <span className="memo-change-arrow" aria-hidden="true">→</span>}
                          <div className={record.current ? "memo-change-step current" : "memo-change-step"}>
                            <div>{record.value}</div>
                            <div className="memo-change-source">{record.source || "Source unavailable"}{record.current ? " · current" : ""}</div>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )) : <div className="memo-empty">No important changes recorded yet.</div>}
              </section>
            );
          })}
          {tab === "memo" && wsIds.every((ws) => !data.memos[ws] || !Object.keys(data.memos[ws].fields || {}).length) && (
            <div className="tert" style={{ fontSize: 12.5 }}>No memo facts yet.</div>
          )}
          {tab === "docs" && (
            <>
              {uniqueDocuments.map((document, index) => (
                <div key={`${document.name || document.doc_type}-${index}`} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, padding: "4px 0", borderBottom: "1px solid var(--bd)", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600 }}>{document.name || document.doc_type}</span>
                  <span className="tchip tert">{document.doc_type}</span>
                  <span className="mono tert">v{document.current_version}</span>
                  <span className="tert" style={{ fontSize: 11.5 }}>{(document.versions || []).length} version{(document.versions || []).length === 1 ? "" : "s"}</span>
                  <span className="tracker-workstream">{document.workstreamIds.map(p.wsName).join(" · ")}</span>
                  {document.authority_conflict && (
                    <span className="pill" style={{ background: "var(--subtle)", color: "var(--red)" }}>authority conflict — lawyer decides</span>
                  )}
                </div>
              ))}
              {!uniqueDocuments.length && <div className="tert" style={{ fontSize: 12.5 }}>No documents registered yet.</div>}
            </>
          )}
          {tab === "track" && trackerView === "all" && (
            <>
              <div className="tracker-intro">All meaningful dates and events, newest to oldest.</div>
              <section className="tracker-group">
                {datedEvents.map((entry, index) => (
                  <div className="tracker-event" key={`${entry.date}-${entry.event}-${index}`}>
                    <TrackerEventBody entry={entry} wsName={p.wsName} />
                  </div>
                ))}
              </section>
              {!datedEvents.length && (
                <div className="tert" style={{ fontSize: 12.5 }}>Nothing tracked yet.</div>
              )}
            </>
          )}
          {tab === "track" && trackerView === "workstream" && (
            <>
              <div className="tracker-intro">The same newest-to-oldest timeline, grouped by the workstream each event affects.</div>
              {wsIds.map((wsId) => {
                const entries = datedEvents.filter((entry) => entry.workstreamIds.includes(wsId));
                if (!entries.length) return null;
                return (
                  <section className="tracker-group" key={wsId}>
                    <div className="tracker-group-heading"><span>{p.wsName(wsId)}</span><span>{entries.length}</span></div>
                    {entries.map((entry, index) => (
                      <div className="tracker-event" key={`${wsId}-${entry.date}-${entry.event}-${index}`}>
                        <TrackerEventBody entry={entry} wsName={p.wsName} currentWsId={wsId} />
                      </div>
                    ))}
                  </section>
                );
              })}
              {!datedEvents.length && <div className="tert" style={{ fontSize: 12.5 }}>Nothing tracked yet.</div>}
            </>
          )}
          {tab === "checks" && (
            <>
              {fired.map((c) => {
                const tau = p.tauFor(c.id);
                return (
                  <div key={c.id + (c.wsId || "")} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--bd)" }}>
                    <span style={{ fontWeight: 600, minWidth: 150 }}>{p.agentName(c.id)}</span>
                    {c.wsId && <span className="tchip tert">{p.wsName(c.wsId)}</span>}
                    <span className="mono" style={{ color: c.parked ? AMBER : c.ok ? "var(--green)" : "var(--red)", whiteSpace: "nowrap" }}>
                      {c.parked ? "parked" : c.ok ? "ok" : "error"} {c.conf.toFixed(2)}
                      {tau != null ? `/τ${tau.toFixed(2)}` : ""}
                    </span>
                    <span className="tert" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.out}>
                      {c.out}
                    </span>
                  </div>
                );
              })}
              {skipped.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <button className="lnk" style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => setSkipsOpen((v) => !v)}>
                    {skipsOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    {skipped.length} skipped — triggers did not fire
                  </button>
                  {skipsOpen &&
                    skipped.map((c) => (
                      <div key={c.id + (c.wsId || "")} className="tert" style={{ fontSize: 12, padding: "3px 0 3px 18px" }}>
                        <span style={{ fontWeight: 600 }}>{p.agentName(c.id)}</span> — {c.reason || "trigger did not fire"}
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
          {tab === "usage" && (
            <>
              {Object.entries(data.usage.perAgent)
                .sort((a, b) => b[1].cost - a[1].cost)
                .map(([id, u]) => (
                  <div key={id} style={{ display: "flex", gap: 8, fontSize: 12.5, padding: "4px 0", borderBottom: "1px solid var(--bd)" }}>
                    <span style={{ fontWeight: 600, flex: 1 }}>{p.agentName(id)}</span>
                    <span className="tert">{u.calls} call{u.calls === 1 ? "" : "s"}</span>
                    <span className="tchip tert">{u.model}</span>
                    <span className="mono">{fmtUsd(u.cost)}</span>
                  </div>
                ))}
              <div style={{ display: "flex", gap: 8, fontSize: 13, padding: "8px 0", fontWeight: 600 }}>
                <span style={{ flex: 1 }}>Total</span>
                <span className="mono">{fmtUsd(data.usage.cost)}</span>
              </div>
            </>
          )}
          {(p.extraTabs || []).map((t) => (tab === t.id ? <React.Fragment key={t.id}>{t.render()}</React.Fragment> : null))}
        </div>
      </div>
    </div>
  );
}
